/**
 * Auswahlregeln der Stimmabgabe (§11.3, §11.4, §11.5).
 *
 * **Dieses Modul ist die einzige Stelle, an der die Regeln stehen.** Die Oberfläche nutzt es zur
 * Benutzerführung, das Backend zur verbindlichen Prüfung (§13.1, §25.2). Läge die Matrix zweimal
 * vor, müsste sie zweimal richtig sein — hier kann sie nur gemeinsam richtig oder gemeinsam
 * falsch sein, und ein Test deckt beide Seiten ab.
 *
 * Eine unzulässige Auswahl wird **abgewiesen**, nicht umgedeutet: eine Ersatzkategorie „ungültig"
 * gibt es nicht (§11.5).
 */
/** Warum eine Auswahl unzulässig ist. Die Oberfläche kann daran ihre Führung ausrichten. */
export const AUSWAHL_FEHLERGRUENDE = [
    'keine_auswahl',
    'zu_viele',
    'zu_wenige',
    'anzahl_nicht_erreicht',
    'sonderoption_kombiniert',
    'mehrere_sonderoptionen',
];
const GUELTIG = { gueltig: true };
function ungueltig(grund, meldung) {
    return { gueltig: false, grund, meldung };
}
/**
 * Prüft eine Auswahl anhand der Arten der gewählten Möglichkeiten.
 *
 * Die vollständige Matrix aus §11.3 und §11.4:
 *
 * | Auswahlart          | zulässig                                                       |
 * | ------------------- | -------------------------------------------------------------- |
 * | Einfachauswahl      | genau eine Möglichkeit, regulär oder Sonderoption              |
 * | Mehrfach „bis zu X" | eine Sonderoption allein oder 1 bis X reguläre Möglichkeiten   |
 * | Mehrfach „genau X"  | eine Sonderoption allein oder genau X reguläre Möglichkeiten   |
 *
 * Durchgängig gilt: Sonderoptionen nie mit regulären Möglichkeiten kombiniert und nie beide
 * zusammen (§11.2, §11.4).
 *
 * Doppelte Nennung **derselben** Möglichkeit lässt sich hier nicht erkennen — dafür braucht es
 * die Kennungen. Der Server prüft das eigens, bevor er hierher kommt.
 */
export function pruefeAuswahl(regeln, gewaehlt) {
    if (gewaehlt.length === 0) {
        return ungueltig('keine_auswahl', 'Bitte wählen Sie eine Möglichkeit aus.');
    }
    const sonderoptionen = gewaehlt.filter((art) => art !== 'regulaer');
    const regulaere = gewaehlt.filter((art) => art === 'regulaer');
    if (sonderoptionen.length > 1) {
        return ungueltig('mehrere_sonderoptionen', '„Nein" und „Enthaltung" können nicht gemeinsam gewählt werden.');
    }
    if (sonderoptionen.length === 1 && regulaere.length > 0) {
        return ungueltig('sonderoption_kombiniert', 'Eine Sonderoption kann nicht mit anderen Möglichkeiten zusammen gewählt werden.');
    }
    if (regeln.auswahlart === 'einfach') {
        return gewaehlt.length === 1
            ? GUELTIG
            : ungueltig('zu_viele', 'Es ist genau eine Möglichkeit auszuwählen.');
    }
    // §11.4: Bei Mehrfachauswahl gilt eine einzelne Sonderoption als vollständige Stimmabgabe —
    // ausdrücklich auch bei „Genau X".
    if (sonderoptionen.length === 1) {
        return GUELTIG;
    }
    const anzahl = regeln.stimmenanzahl ?? 1;
    if (regeln.auswahlregel === 'genau') {
        if (regulaere.length === anzahl) {
            return GUELTIG;
        }
        return ungueltig('anzahl_nicht_erreicht', `Es sind genau ${anzahl} Möglichkeiten auszuwählen; derzeit ${regulaere.length}.`);
    }
    if (regulaere.length > anzahl) {
        return ungueltig('zu_viele', `Es sind höchstens ${anzahl} Möglichkeiten auswählbar.`);
    }
    return GUELTIG;
}
/**
 * Wendet einen Klick auf die bisherige Auswahl an (§11.4).
 *
 * Setzt die gegenseitige Ausschließlichkeit um: Die Auswahl einer Sonderoption entfernt alle
 * regulären Auswahlen, die Auswahl einer regulären Möglichkeit entfernt eine zuvor gewählte
 * Sonderoption. Bei Einfachauswahl ersetzt jeder Klick die bisherige Auswahl.
 *
 * Reine Funktion ohne Nebenwirkung — sie gibt die neue Auswahl zurück und verändert nichts.
 * Verbindlich bleibt {@link pruefeAuswahl} auf dem Server: dass die Oberfläche gar nicht erst in
 * einen unzulässigen Zustand gerät, ist Bequemlichkeit, keine Autorisierung (§25.2).
 */
export function wendeAuswahlAn(regeln, bisher, geklickt) {
    if (regeln.auswahlart === 'einfach') {
        return [geklickt];
    }
    const warGewaehlt = bisher.some((option) => option.id === geklickt.id);
    if (warGewaehlt) {
        return bisher.filter((option) => option.id !== geklickt.id);
    }
    if (geklickt.art !== 'regulaer') {
        // Eine Sonderoption steht immer allein.
        return [geklickt];
    }
    const ohneSonderoptionen = bisher.filter((option) => option.art === 'regulaer');
    return [...ohneSonderoptionen, geklickt].sort((a, b) => a.position - b.position);
}
//# sourceMappingURL=auswahlregeln.js.map